import type { MouseEvent } from 'react';
import styled from 'styled-components';
import type { DoubleBannerType } from '@amplience/content-types/typings/c-double-banner';
import { maxWidthPartial, media, spacing } from '@storefront/lib-global/stylings';
import { sendSelectBannerEvent } from '@storefront/lib-global/utils/gtm/events/select_banner/sendSelectBannerEvent';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import { BannerTypes } from '../../utils';
import MultipleOverlappingBanner from './MultipleOverlappingBanner';
import OverlappingBanner from './OverlappingBanner';
import TwoColumnBanner from './TwoColumnBanner';
import TwoColumnLongMediaBanner from './TwoColumnLongMediaBanner';
import type { DoubleBannerProps } from './bannerType';

const S = {
  DoubleBannerContainer: styled.div<{ $bgColor?: string }>`
    background-color: ${({ $bgColor }) => $bgColor};
    padding: 0 ${spacing.S};

    & *:empty {
      display: none;
    }
    ${maxWidthPartial({ maxWidth: 1440, withPaddingXL: true })}

    @media ${media.greaterThan('xx')} {
      max-width: 1300px;
    }
  `,
};

const Banners = {
  [BannerTypes.TWO_COLUMN]: TwoColumnBanner,
  [BannerTypes.TWO_COLUMN_LONG_MEDIA]: TwoColumnLongMediaBanner,
  [BannerTypes.OVERLAPPING]: OverlappingBanner,
  [BannerTypes.MULTIPLE_OVERLAPPING]: MultipleOverlappingBanner,
};

export function DoubleBannerComponent({ bannerType, backgroundColor, ...props }: DoubleBannerProps) {
  const Component = Banners[bannerType as DoubleBannerType];

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    const element = event.currentTarget as HTMLAnchorElement;

    sendSelectBannerEvent({
      component_header: getLocalizedValue(props.bannerTitle?.text ?? '').trim(),
      component_type: `${bannerType ?? ''} Banner`.trim(),
      link_url: element.href,
      link_text: element.textContent ?? '',
    });
  };

  return (
    <S.DoubleBannerContainer $bgColor={backgroundColor}>
      <Component {...props} onClick={handleClick} />
    </S.DoubleBannerContainer>
  );
}

export default DoubleBannerComponent;
